/**
 * How it works: the client-facing explanation of the rules and the maths.
 *
 * This is a teaching surface, not a reporting one. It reads no live data on
 * purpose, so a demo mid-flight can never make the explanation contradict
 * itself, and the calculators use the real domain functions so the page cannot
 * drift away from the product it is describing.
 *
 * The technical companion is docs/RULES-AND-CALCULATIONS.md, which carries the
 * same rules with file and line references.
 */
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Compass, ListTodo, Wallet, Calculator, Route, HelpCircle } from 'lucide-react';
import DayMoneyCalculator from '@/components/howitworks/DayMoneyCalculator';
import QueueLaneSimulator from '@/components/howitworks/QueueLaneSimulator';
import DepositLedgerPlayground from '@/components/howitworks/DepositLedgerPlayground';
import JourneyStepper from '@/components/howitworks/JourneyStepper';
import { Callout, Formula, RuleCard, Section } from '@/components/howitworks/primitives';

const TABS = [
  { key: 'start', label: 'Start here', Icon: Compass },
  { key: 'queue', label: 'The queue', Icon: ListTodo },
  { key: 'deposits', label: 'Deposits', Icon: Wallet },
  { key: 'money', label: "The day's money", Icon: Calculator },
  { key: 'journeys', label: 'Walkthroughs', Icon: Route },
  { key: 'confirm', label: 'Over to you', Icon: HelpCircle },
] as const;

type TabKey = (typeof TABS)[number]['key'];
const TAB_KEYS = new Set<string>(TABS.map((t) => t.key));

export default function HowItWorks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab') ?? 'start';
  const tab: TabKey = (TAB_KEYS.has(raw) ? raw : 'start') as TabKey;

  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <section className="px-8 py-8" data-testid="how-it-works">
      <header className="max-w-3xl">
        <p className="label-small-caps text-gold-bright">How it works</p>
        <h1 className="heading-display text-3xl text-cream mt-2">The rules behind the screens</h1>
        <p className="font-serif text-cream-soft mt-3 leading-relaxed">
          Every list in this platform is worked out rather than typed in, and every dollar figure comes from a formula
          somebody has to agree with. This is that reasoning, in plain English, with the sums you can pull apart
          yourself.
        </p>
      </header>

      <nav
        className="mt-6 flex flex-wrap gap-1.5 border-b border-gold-dim/20 pb-3 max-w-6xl"
        role="tablist"
        data-testid="how-it-works-tabs"
      >
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`how-it-works-tab-${t.key}`}
              className={clsx(
                'rounded-full border px-4 py-1.5 font-inter text-xs uppercase tracking-wider transition',
                active
                  ? 'border-gold-bright bg-gold/10 text-gold-bright'
                  : 'border-transparent text-cream-soft hover:border-gold-dim/40 hover:text-cream',
              )}
            >
              <span className="inline-flex items-center gap-2">
                <t.Icon size={12} /> {t.label}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-7 max-w-6xl">
        {tab === 'start' ? <StartHere /> : null}
        {tab === 'queue' ? <QueueTab /> : null}
        {tab === 'deposits' ? <DepositsTab /> : null}
        {tab === 'money' ? <MoneyTab /> : null}
        {tab === 'journeys' ? <JourneysTab /> : null}
        {tab === 'confirm' ? <ConfirmTab /> : null}
      </div>
    </section>
  );
}

/** The two-line framing block that opens every tab. */
function TabIntro({ answers, powers }: { answers: string; powers: string }) {
  return (
    <div className="grid gap-4 rounded-lg border border-gold-dim/25 bg-ink-deep px-5 py-4 sm:grid-cols-2">
      <div>
        <p className="label-small-caps text-gold-bright">This section answers</p>
        <p className="font-serif text-cream mt-1.5 italic leading-relaxed">{answers}</p>
      </div>
      <div>
        <p className="label-small-caps text-cream-faint">What it changes for you</p>
        <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{powers}</p>
      </div>
    </div>
  );
}

function StartHere() {
  return (
    <>
      <TabIntro
        answers="If nobody types up a to-do list, how does the right work reach the right person?"
        powers="Understanding these five ideas makes every other screen predictable."
      />

      <Section
        eyebrow="The short version"
        title="Five ideas everything else follows from"
        lede="Learn these and the rest of the platform stops being surprising."
      >
        <div className="space-y-3">
          <RuleCard
            title="Nothing is a list somebody maintains"
            rule="There is no queue table anyone adds to or ticks off. Every list you see is worked out fresh from what is true right now: who has paid, who has messaged, who has not been called back."
            why="Lists that people maintain go stale the moment somebody is busy. A list that is worked out cannot go stale, and it cannot be gamed by ticking things off."
          />
          <RuleCard
            title="Money in the door is never given up on"
            tone="mint"
            rule="Anyone who has paid you, or who has a live payment link out, is protected. They can never fall off the board onto the nurture list no matter how many times you fail to reach them."
            why="Everyone else gets three attempts and then moves to the nurture list. Applying that to somebody holding your money is how deposits quietly rot."
          />
          <RuleCard
            title="A deposit is a timing device, not income"
            tone="violet"
            rule="Taking a deposit changes when money arrives. It never changes what a session costs, and it never changes what an artist earns."
            why="It is the single most common misreading of a booking system. Once you see the deposit as timing rather than revenue, roll-forwards, top-ups and forfeits all behave the way you would expect."
          />
          <RuleCard
            title="The till should end the day near empty"
            tone="orange"
            rule="Cash coming in over the day is there to pay artists their envelopes at their finish. The system steers clients towards cash while the day is still short, and towards card once it is covered."
            why="Artists are paid in cash at the end of their shift. If everything went on card there would be nothing in the drawer to pay them with, and you would be dipping into the float."
          />
          <RuleCard
            title="Nothing is deleted, only superseded"
            tone="azure"
            rule="Reschedule a booking and the original is kept. Forfeit a deposit and the movement is written as a new line rather than an edit. Reverse it and that is another line."
            why="You can always answer the question 'what actually happened here', which matters when a client disputes something months later."
          />
        </div>
      </Section>

      <Section
        eyebrow="Orientation"
        title="Where each rule shows up"
        lede="Four surfaces do the work. Everything else reports on them."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <WhereCard
            name="My Queue"
            who="Bookings agents"
            does="The single prioritised list of who to contact and why. Three lanes, worked top down."
          />
          <WhereCard
            name="Sweeper"
            who="Georgina"
            does="Finds empty days across every studio and suggests who to offer them to."
          />
          <WhereCard
            name="Confirmations"
            who="Managers"
            does="The week ahead. Two touches per booking, then a chase list for anyone who has not answered."
          />
          <WhereCard
            name="The floor and the money screens"
            who="Managers and the owner"
            does="Checkout, artist envelopes, the till, and the two reconciliations that prove the day."
          />
        </div>
      </Section>
    </>
  );
}

function WhereCard({ name, who, does }: { name: string; who: string; does: string }) {
  return (
    <div className="panel-dark p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-inter text-sm font-semibold text-cream">{name}</h3>
        <span className="font-inter text-[11px] uppercase tracking-wider text-gold-bright">{who}</span>
      </div>
      <p className="font-serif text-cream-soft mt-1.5 text-sm leading-relaxed">{does}</p>
    </div>
  );
}

function QueueTab() {
  return (
    <>
      <TabIntro
        answers="Why is this person at the top of my list, and why has that other one dropped off it?"
        powers="Agents stop deciding who to ring next, and start just working down the page."
      />

      <Section eyebrow="The structure" title="Three lanes, worked in order">
        <div className="grid gap-3 sm:grid-cols-3">
          <LaneCard
            tone="azure"
            name="Fresh contact"
            rule="Somebody just reached out and nobody has answered."
            detail="Worked strictly oldest first, ignoring everything else. A quiet enquiry from this morning is answered before a livelier one from this afternoon."
          />
          <LaneCard
            tone="gold"
            name="Money"
            rule="A deposit is paid with no date, or a payment link is running out."
            detail="Ranked by urgency. This lane is protected: nobody in it can ever be written off as unreachable."
          />
          <LaneCard
            tone="violet"
            name="Open business"
            rule="An unfinished piece, a promised rebook, a healing check that is due."
            detail="Real work, none of it urgent. It waits behind the first two lanes and loses priority slowly rather than expiring."
          />
        </div>

        <div className="mt-4">
          <Callout tone="gold" title="What sits above all three">
            A scheduled call that is due inside fifteen minutes jumps everything and appears as a banner at the top of
            the screen. It is the only thing that outranks fresh contact.
          </Callout>
        </div>
      </Section>

      <Section eyebrow="Have a go" title="Where would this land?">
        <QueueLaneSimulator />
      </Section>

      <Section
        eyebrow="The contact clock"
        title="Three attempts, then it stops being a person's job"
        lede="One clock governs how often anybody is contacted, and it is shared by every screen."
      >
        <div className="space-y-3">
          <RuleCard
            title="Each attempt buys 24 hours of quiet"
            rule="Call or message somebody and their card drops to the bottom of the board for a day. When the day is up, it comes back at the next attempt."
            why="It stops two agents ringing the same person the same afternoon, without anyone having to coordinate."
          />
          <RuleCard
            title="A reply resets everything"
            tone="mint"
            rule="The moment a client writes back, they go straight back to a first attempt with a clean slate, even if they were already written off."
            why="Someone who has just replied is the warmest lead you have. Treating them as a third strike would be backwards."
          />
          <RuleCard
            title="Three strikes moves them to the nurture list"
            tone="blood"
            rule="After a third unanswered attempt, the card moves to a red list that a person no longer works. If somebody actively says no, it goes to a separate collapsed list with no contact buttons at all."
            why="There is a real difference between 'we cannot reach them' and 'they told us no'. The second should not be sitting in front of an agent with a call button next to it."
            detail={
              <Callout tone="mint">
                The exception is the money lane. Anyone holding a deposit or a live payment link re-arms at the third
                attempt and stays on the board indefinitely.
              </Callout>
            }
          />
        </div>
      </Section>

      <Section
        eyebrow="Filling empty days"
        title="The Sweeper, and the three week rule"
        lede="Sweeper finds days that are not going to pay for themselves and suggests who to offer them to."
      >
        <div className="space-y-3">
          <RuleCard
            title="Candidates come in three groups, in this order"
            rule="First, people already booked further out who could be brought forward. Then anyone holding a deposit with no date. Then past clients of that artist."
            why="Bringing a future booking forward frees up the later slot too, so it is worth the most. A stale deposit is the most urgent to convert. Past clients are the widest pool and the coldest."
          />
          <RuleCard
            title="Anyone tattooed within three weeks is excluded outright"
            tone="blood"
            rule="If a client had a session finish within twenty-one days either side of the gap, they do not appear as a candidate at all."
            why="Skin needs to heal. Offering somebody a chair a fortnight after their last sitting is not a deal you want your team making to fill a Tuesday."
            detail={
              <Formula label="The filter">
                {`exclude if any completed session is within 21 days
of the empty day, before OR after it`}
              </Formula>
            }
          />
          <RuleCard
            title="A candidate who does not fit is demoted, never hidden"
            rule="If someone's booking is four hours and the gap is three, they drop to the bottom of their group rather than vanishing."
            why="You asked for this specifically. A part fill is better than an empty chair, and that is a judgement for the person on the phone, not the software."
          />
        </div>
      </Section>
    </>
  );
}

function LaneCard({
  tone,
  name,
  rule,
  detail,
}: {
  tone: 'azure' | 'gold' | 'violet';
  name: string;
  rule: string;
  detail: string;
}) {
  const ring =
    tone === 'azure'
      ? 'border-azure/40 bg-azure/5'
      : tone === 'gold'
        ? 'border-gold-dim/45 bg-gold/5'
        : 'border-violet/40 bg-violet/5';
  const text = tone === 'azure' ? 'text-azure' : tone === 'gold' ? 'text-gold-bright' : 'text-violet';
  return (
    <div className={clsx('rounded-lg border p-4', ring)}>
      <p className={clsx('label-small-caps', text)}>{name}</p>
      <p className="font-serif text-cream mt-2 leading-relaxed">{rule}</p>
      <p className="font-serif text-cream-faint mt-2 text-sm leading-relaxed">{detail}</p>
    </div>
  );
}

function DepositsTab() {
  return (
    <>
      <TabIntro
        answers="What is a deposit worth right now, and what happens to it when plans change?"
        powers="Every argument about deposits becomes a question you can answer from one screen."
      />

      <Section
        eyebrow="The core idea"
        title="A deposit is the sum of its history"
        lede="It is not a number sitting on a record. It is everything that ever happened to it, added up."
      >
        <Formula label="What a deposit is worth">
          {`  paid in
+ topped up
+ rolled in from an earlier booking
+ reinstated after a forfeit
- rolled forward to a later booking
- forfeited

refunds and transfers are tracked but deliberately not counted`}
        </Formula>
        <p className="font-serif text-cream-soft mt-3 leading-relaxed max-w-3xl">
          A refund is money leaving the business, so counting it as credit the client still holds would be wrong. That
          is the only subtlety in the whole calculation, and it is worth knowing about before somebody asks.
        </p>
      </Section>

      <Section eyebrow="Have a go" title="Build a deposit's history">
        <DepositLedgerPlayground />
      </Section>

      <Section eyebrow="The clocks" title="How long a link stays alive">
        <div className="space-y-3">
          <RuleCard
            title="24 hours if the session is close, 48 if it is not"
            rule="A payment link for a session inside the next week expires after a day. Anything further out gets two days."
            why="A slot three days away cannot sit reserved for forty-eight hours while somebody thinks about it. A slot three months away easily can."
          />
          <RuleCard
            title="An expired link releases the chair immediately"
            rule="The hold comes off the calendar the moment the deadline passes, so the slot is sellable again. The deposit record stays, marked expired, and a resend job appears in the queue for seven days."
            why="The chair is the scarce thing. Holding it open for somebody who has stopped replying costs you the booking you could have taken instead."
          />
          <RuleCard
            title="Money rolled forward is good for thirty days"
            tone="violet"
            rule="When a client rebooks or says they will rebook later, their deposit moves into a fresh pot with a thirty day window to find a date."
            why="It keeps the pressure on to actually book something, without the client feeling their money is at risk the moment they leave."
          />
        </div>
      </Section>

      <Section
        eyebrow="Partial deposits"
        title="Paying in instalments"
        lede="If somebody was asked for $400 and paid $250, they are not a problem to be reconciled. They are a client on a payment plan."
      >
        <div className="space-y-3">
          <RuleCard
            title="Partial is never a flag anyone sets"
            rule="It is simply what happens when the sum of what has been paid is less than what was asked for. Nobody marks a deposit as partial."
            why="Flags need maintaining and go wrong. A number that is derived cannot disagree with itself."
          />
          <RuleCard
            title="Reminders go weekly, then once more a week out"
            rule="A nudge every seven days, plus a final one inside the last week before the session."
            why="Enough to be useful, not enough to be nagging."
          />
          <RuleCard
            title="If the client has just written to you, the nudge is held"
            tone="mint"
            rule="When the most recent message in the thread came from them, no automated reminder is sent."
            why="Nothing makes a business look less like it is paying attention than an automated chase arriving the day after a real conversation."
          />
        </div>
      </Section>
    </>
  );
}

function MoneyTab() {
  return (
    <>
      <TabIntro
        answers="Who is owed what at the end of today, and will there be enough in the drawer to pay them?"
        powers="The cash steer on the floor stops being a mystery and starts being something you can check."
      />

      <Section eyebrow="Have a go" title="The whole money spine in one widget">
        <DayMoneyCalculator />
      </Section>

      <Section eyebrow="The split" title="What an artist earns">
        <div className="space-y-3">
          <RuleCard
            title="Commission is worked out on the session price"
            rule="Never on what the client actually handed over on the day. If the price is $1,000 and they paid $200 of it months ago, the split still runs on $1,000."
            why="Otherwise the artist's pay would depend on when the client chose to pay, which is nothing to do with the artist."
            detail={
              <Formula label="Three arrangements, all supported">
                {`percentage      envelope = session price x their share  (rounded to the dollar)
owner artist    envelope = the whole session price
flat day fee    envelope = the fee, if they worked at all that day

house share = session price - envelope`}
              </Formula>
            }
          />
          <RuleCard
            title="Merch is the studio's, whole"
            rule="Anything sold on the way out sits outside the commissionable total entirely."
            why="It is stock the studio bought. It is not the artist's work."
          />
          <RuleCard
            title="Nothing else is deducted"
            rule="There is no supplies charge, no chair rent, no tax withheld. The only reduction is the house share of the split."
            why="Worth stating plainly, because most studio software assumes deductions exist. If you want any, that is a conversation to have."
          />
        </div>
      </Section>

      <Section
        eyebrow="The till"
        title="Cash, and why the floor asks for it"
        lede="Artists are handed a cash envelope when they finish. That cash has to come from somewhere."
      >
        <div className="space-y-3">
          <RuleCard
            title="The rule is one line"
            tone="orange"
            rule="If what the artists are owed is more than what is in the till, ask the next client for cash. Otherwise card is fine."
            detail={<Formula>{`payouts owed > cash in the till   ->  ask for cash
otherwise                         ->  card is fine`}</Formula>}
            why="It is deliberately simple, so anyone on the floor can sanity check it in their head."
          />
          <RuleCard
            title="The float does not count as money you can spend"
            rule="Every figure that decides the steer leaves the opening float out. It is a sealed reserve, not working cash."
            why="If the float counted, the system would tell you the day was covered right up until the moment you emptied your emergency money."
          />
          <RuleCard
            title="An artist is paid whole, or not at all"
            rule="If the till cannot cover an envelope, nobody is part-paid. The whole day waits and goes out with the next payment that covers it, which is why one payment can settle yesterday and today together. Dipping into the float to pay in full is possible, but it is a separate flagged movement with a reason attached."
            why="A part payment leaves an artist tracking a remainder nobody owns. A whole day that waits is a single number they can see on their own screen until it is paid, and the studio can see it ageing."
          />
        </div>
      </Section>

      <Section
        eyebrow="Proving the day"
        title="Two reconciliations, two different questions"
        lede="They sound like the same job and they are not."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="panel-dark p-5">
            <p className="label-small-caps text-gold-bright">The sale lane</p>
            <p className="font-serif text-cream mt-2 leading-relaxed">
              Did every sale we rang up actually reach the bank?
            </p>
            <p className="font-serif text-cream-faint mt-2 text-sm leading-relaxed">
              Matches each card sale to its docket, and flags anything missing, mismatched or unnamed. A sale taken
              after 5pm is not an exception, it just banks tomorrow.
            </p>
          </div>
          <div className="panel-dark p-5">
            <p className="label-small-caps text-gold-bright">The deposit lane</p>
            <p className="font-serif text-cream mt-2 leading-relaxed">
              Did every deposit we were told about actually land?
            </p>
            <p className="font-serif text-cream-faint mt-2 text-sm leading-relaxed">
              Catches the two failure modes that cost real money: it is in the bank but not in the system, or it is in
              the system but never reached the bank.
            </p>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <RuleCard
            title="A partial deposit is never an exception"
            rule="Asked for $400, received $250, so $150 is outstanding. That is a top-up to chase, not a reconciliation problem."
            why="Mixing the two turns a healthy payment plan into a red flag on a finance screen."
          />
          <RuleCard
            title="Nobody signs off their own resolution"
            tone="blood"
            rule="When somebody proposes a fix for a mismatched line, it needs a second person to approve it, and the system blocks self approval before it even checks anything else."
            why="It is the one place in the platform where a single person could quietly make money disappear."
          />
          <RuleCard
            title="Closing the day freezes it"
            rule="Counting the till and closing takes a snapshot of every payment, docket and ledger figure. Reopening needs a typed reason, keeps the original close on record, and puts the day back to uncounted."
            why="A day that can be silently re-edited after the fact is not a record of anything."
          />
        </div>
      </Section>
    </>
  );
}

function JourneysTab() {
  return (
    <>
      <TabIntro
        answers="What actually happens after I press the button?"
        powers="Training a new starter stops being a shadowing exercise."
      />

      <Section eyebrow="Step through it" title="The journeys with the most moving parts">
        <JourneyStepper />
      </Section>

      <Section
        eyebrow="Reference"
        title="The clocks, side by side"
        lede="Several rules use similar looking numbers for completely different reasons. This is the whole set in one place."
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr className="border-b border-gold-dim/25">
                <th className="label-small-caps text-cream-faint py-2 pr-4">Clock</th>
                <th className="label-small-caps text-cream-faint py-2 pr-4">Length</th>
                <th className="label-small-caps text-cream-faint py-2">What it is for</th>
              </tr>
            </thead>
            <tbody>
              {CLOCKS.map((c) => (
                <tr key={c.name} className="border-b border-gold-dim/12">
                  <td className="py-2.5 pr-4 font-inter text-sm text-cream align-top">{c.name}</td>
                  <td className="py-2.5 pr-4 data-mono text-sm text-gold-bright align-top whitespace-nowrap">
                    {c.length}
                  </td>
                  <td className="py-2.5 font-serif text-sm text-cream-soft leading-relaxed">{c.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Callout tone="azure" title="Why three healing numbers">
            Twenty-one days keeps a recently tattooed client out of gap-filling offers. Fourteen days is the earliest a
            rebook will be suggested. Seven days is when someone rings to ask how it settled. They are three different
            jobs that happen to sit near each other.
          </Callout>
        </div>
      </Section>
    </>
  );
}

const CLOCKS: ReadonlyArray<{ name: string; length: string; purpose: string }> = [
  { name: 'Contact clock', length: '24 hours', purpose: 'How long after an attempt before the same person resurfaces.' },
  {
    name: 'Payment link, near date',
    length: '24 hours',
    purpose: 'How long a chair is held when the session is inside a week.',
  },
  { name: 'Payment link, far date', length: '48 hours', purpose: 'The same hold when the session is further out.' },
  { name: 'Resend window', length: '7 days', purpose: 'How long an expired link stays worth chasing.' },
  { name: 'Confirmation call', length: '7 days out', purpose: 'The last point a cancelled slot can realistically be refilled.' },
  { name: 'Confirmation text', length: '2 days out', purpose: 'Close enough that plans are firm, far enough to react.' },
  { name: 'Waiver dispatch', length: '4 days out', purpose: 'Sent close enough to the day that people actually sign it.' },
  { name: 'Healing check', length: '7 days after', purpose: 'A natural reason to call, and an opening to talk about the next sitting.' },
  { name: 'Rebook earliest', length: '14 days after', purpose: 'The soonest a next session will be offered.' },
  { name: 'Gap fill exclusion', length: '21 days either side', purpose: 'Keeps recently tattooed clients out of empty-chair offers.' },
  { name: 'Rolled deposit', length: '30 days', purpose: 'How long money carried forward has to find a date.' },
  { name: 'Cash collection', length: 'by 6pm', purpose: 'Every artist is paid out by clock-off, whatever time they finish.' },
  { name: 'Banking cutoff', length: '5pm', purpose: 'Sales after this bank the following day. Not a problem, just later.' },
];

function ConfirmTab() {
  return (
    <>
      <TabIntro
        answers="What has been decided on my behalf, and what still needs my call?"
        powers="Signing these off is what turns the prototype's behaviour into your policy."
      />

      <Section
        eyebrow="Be aware"
        title="Decisions taken to keep things moving"
        lede="Each of these was a judgement call made during the build so the work could continue. None of them are hard to change, and all of them are worth a minute of your attention."
      >
        <div className="space-y-2.5">
          {ASSUMPTIONS.map((a) => (
            <div key={a.q} className="panel-dark px-5 py-4">
              <p className="font-inter text-sm font-semibold text-cream">{a.q}</p>
              <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">
                <span className="label-small-caps text-gold-bright/80">Currently </span>
                {a.current}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Needs a decision"
        title="The open questions"
        lede="These are the ones where the answer changes behaviour rather than just a number."
      >
        <div className="space-y-3">
          {OPEN.map((o) => (
            <div key={o.q} className="rounded-lg border border-orange/40 bg-orange/10 px-5 py-4">
              <p className="font-inter text-sm font-semibold text-cream">{o.q}</p>
              <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{o.detail}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Reading further" title="If you want the exact detail">
        <Callout tone="gold">
          Everything on this page has a technical companion that carries the same rules with the precise thresholds and
          where each one is enforced. Ask for the rules and calculations reference and we will walk it through together.
        </Callout>
      </Section>
    </>
  );
}

const ASSUMPTIONS: ReadonlyArray<{ q: string; current: string }> = [
  {
    q: 'Artists are all paid out by 6pm, whatever time they actually finish',
    current:
      'Every envelope is treated as due by clock-off, even for an artist rostered later. Sales after 5pm bank the next day.',
  },
  {
    q: 'A client owes the session price less whatever deposit is on file',
    current: 'The cash forecast assumes nothing else changes between now and the counter.',
  },
  {
    q: 'Evening clients are assumed to pay around 6pm at check-in',
    current: 'Used only to forecast whether the day will be covered, not to charge anybody earlier.',
  },
  {
    q: 'A no-show forfeits the deposit immediately, and the same way at every studio',
    current: 'Reversible by Georgina or the owner, with a reason recorded.',
  },
  {
    q: 'Envelopes are rounded to the whole dollar',
    current: 'A 60% split of $1,675 pays $1,005 rather than $1,005.00.',
  },
  {
    q: 'Repeat client rate is measured across all time, not just the reporting window',
    current: 'It counts anyone who has ever had more than one completed session.',
  },
  {
    q: 'A studio counts as booked ahead at 80% full or better',
    current: 'The weeks-booked-ahead figure counts consecutive weeks from today that clear that bar.',
  },
  {
    q: 'An expired link with no date on record resends without asking about availability',
    current: 'Only links that had a specific slot pinned trigger the date-taken check.',
  },
  {
    q: 'Melbourne time is treated as a flat ten hours ahead',
    current: 'Daylight saving is not modelled. It affects which day a late-night sale lands on.',
  },
];

const OPEN: ReadonlyArray<{ q: string; detail: string }> = [
  {
    q: 'Is there a twelve month expiry on deposits?',
    detail:
      'The deposit aging report is labelled as though there is one, but no such rule is implemented. Either it becomes policy and we build it, or the label comes off. Worth settling, because it changes what the aging buckets mean.',
  },
  {
    q: 'Should the banking cutoff and the payout clock-off stay an hour apart?',
    detail:
      'Sales bank from 5pm, artists are paid by 6pm. They are separate on purpose at the moment, but if your bank behaves differently we should align them.',
  },
  {
    q: 'Are there any deductions before an artist is paid?',
    detail:
      'Right now the only reduction is the house share of the split. No supplies, no chair rent, no withholding. If any of those exist in practice, they need adding.',
  },
  {
    q: 'Confirm the confirmation cadence is two touches, not three',
    detail:
      'A call seven days out and a text two days out, with no message on the day itself. Some earlier notes describe a day-of touch as well. The build follows the two-touch version.',
  },
];
